import React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {RootMachineContext} from '../../machines/rootMachine';
import {SmallCard} from '../../lib/small-card/small-card';
import {CardChildren} from '../../lib/card-children/card-children';
import {MeetingDateTimeInfo} from '../meeting-date-time-info/meeting-date-time-info';

import {
  selectOwner,
  selectError,
  selectIsReschedule,
  selectRescheduleLimit,
} from '../../selectors/commonSelector';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {generateTestId, InContextAlert} from '@spaced-out/ui-design-system';

import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';

import {getReasonFormSchema} from './reason-form.schema.ts';
import {ReasonInput} from './reason-input.tsx';
import {TEST_ID} from '../../utils/testId.ts';

import css from './reason-form.css';


export const ReasonForm = () => {
  const {t} = useLabelTranslation();
  const rootRef = RootMachineContext.useActorRef();
  const state = RootMachineContext.useSelector((state) => state);
  const isReschedule = RootMachineContext.useSelector(selectIsReschedule);
  const error = RootMachineContext.useSelector((state) =>
    selectError(state, t),
  );
  const rescheduleLimit = RootMachineContext.useSelector(selectRescheduleLimit);
  const owner = RootMachineContext.useSelector(selectOwner);

  const isCancellationSubmitting = state.matches({
    cancellationFlow: {reasonForm: 'submitting'},
  });
  const isRescheduleSubmitting = state.matches({
    rescheduleFlow: {reasonForm: 'submitting'},
  });
  const isCancellationError = state.matches({
    cancellationFlow: {reasonForm: 'error'},
  });
  const isRescheduleError = state.matches({
    rescheduleFlow: {reasonForm: 'error'},
  });
  const isError = isCancellationError || isRescheduleError;

  const reasonSchema = getReasonFormSchema(t);

  const {control, handleSubmit, formState} = useForm<
    z.infer<typeof reasonSchema>
  >({
    mode: 'onTouched',
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(reasonSchema),
  });

  const submitForm = (values: z.infer<typeof reasonSchema>) => {
    rootRef.send({type: 'reasonForm.submit', reason: values.reason || ''});
  };

  const errorText = error.errorMessage;
  const isSubmitting = isCancellationSubmitting || isRescheduleSubmitting;
  const disableSubmit = isError && !error.allowRetry;

  const showRescheduleLimit = rescheduleLimit > -1 && isReschedule;
  const actionType = isReschedule ? 'reschedule' : 'cancel';

  return (
    <SmallCard
      icon="calendar"
      testId={generateTestId({
        base: TEST_ID.REASON_FORM,
        slot: 'reason-form-small-card',
      })}
      title={
        isReschedule
          ? t('Reschedule_Meeting', 'Reschedule Meeting')
          : t('Cancel_Meeting', 'Cancel Meeting')
      }
    >
      <MeetingDateTimeInfo />
      {showRescheduleLimit && (
        <InContextAlert
          semantic={'information'}
          testId={generateTestId({
            base: TEST_ID.REASON_FORM,
            slot: 'reason-form-information-alert',
          })}
        >{`${t(
          'Guest_will_get_an_email_to_reschedule_This_meeting_can_be_rescheduled_rescheduleLimit_more_times',
          {rescheduleLimit},
        )}.`}</InContextAlert>
      )}
      {isError && (
        <InContextAlert
          semantic="danger"
          testId={generateTestId({
            base: TEST_ID.REASON_FORM,
            slot: 'reason-form-danger-alert',
          })}
        >
          {/* Note: already getting translated from errorText */}
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          {errorText}
        </InContextAlert>
      )}
      <CardChildren>
        <div className={css.cancelForm}>
          <ReasonInput
            control={control}
            reasonName="reason"
            required={true}
            disabled={isSubmitting}
            actionType={actionType}
            participantType={owner}
            testId={generateTestId({
              base: TEST_ID.REASON_FORM,
              slot: 'reason-form-reason-input',
            })}
          />
          <Button
            isLoading={isSubmitting}
            onClick={handleSubmit(submitForm)}
            disabled={!formState.isValid || disableSubmit}
            testId={generateTestId({
              base: TEST_ID.REASON_FORM,
              slot: 'reason-form-button',
            })}
          >
            {isReschedule
              ? t('Request_Reschedule', 'Request Reschedule')
              : t('Cancel', 'Cancel')}
          </Button>
        </div>
      </CardChildren>
    </SmallCard>
  );
};
